import domainModel from 'domain-model';
import type {
  OrderFullfillmentCenter,
  ArticleReference,
  PricedArticleReference,
  StockKeepingUnit,
} from 'domain-model/types/oms';
import type {
  Cart,
  CartRow,
  CustomerSubUnitInPcsServiceReference,
  ProcurementArticleReference,
  SupplierCustomerInPcsServiceReference,
} from 'domain-model/types/pcs';

import type { Action } from '../types';
import {
  handleCartRowDeletion,
  handleCartRowInsertion,
  handleCartRowUpdate,
} from './handle-ws-cart-update';

export const ACTION_TYPES = {
  PERSISTED_CARTS_LOADED: 'PERSISTED_CARTS_LOADED',
  PERSISTED_CART_ROW_UPDATED: 'PERSISTED_CART_ROW_UPDATED',
  PERSISTED_CART_CREATED: 'PERSISTED_CART_CREATED',
  PERSISTED_CART_COPIED_FROM_ORDER: 'PERSISTED_CART_COPIED_FROM_ORDER',
};

const _ = require('lodash');
const { lodashUUID } = require('lodash-uuid');

_.mixin(lodashUUID);

const { SOCKET } = require('./action-type');

export interface PersistedMultiCartState {
  cartUpdateMessage: string;
  cartUpdateErrorMessages: string[];
  carts: {
    [key: string]: Cart;
  };
  isLoaded?: boolean;
}

const initialState = (): PersistedMultiCartState => ({
  cartUpdateMessage: '',
  cartUpdateErrorMessages: [],
  carts: {},
  isLoaded: false,
});

const getCartMessage = (
  pricedArticle: ArticleReference,
  quantity: number,
  orderedSKU: StockKeepingUnit | undefined,
) => {
  const articleViewModel = domainModel.article.getViewModel(pricedArticle);

  if (quantity === 0) {
    return `${articleViewModel.displayName} har tagits bort från korgen`.trim();
  }

  if (orderedSKU) {
    return `${quantity} ${orderedSKU.name} ${articleViewModel.displayName} har lagts i korgen`.trim();
  }

  const nrOforderUnits = domainModel.order.getNumberOfOrderUnitsOnRow({
    article: pricedArticle,
    deliveredQuantity: quantity,
  });

  let orderUnitDisplay = `${nrOforderUnits} x ${pricedArticle.colliSize}`;

  if (pricedArticle.orderUnit === 'single') {
    orderUnitDisplay = `${nrOforderUnits} st`;
  } else if (pricedArticle.orderUnit === 'weight') {
    orderUnitDisplay = `${nrOforderUnits} kg`;
  }

  return `${orderUnitDisplay} ${articleViewModel.displayName} har lagts i korgen`.trim();
};

// eslint-disable-next-line default-param-last
const persistedMultiCartReducer = (state = initialState(), action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case SOCKET(domainModel.cart.events.CART_CREATED): {
      const { eventParams } = payload;

      if (eventParams.active !== undefined && !eventParams.active) {
        return state;
      }
      return {
        ...state,
        carts: {
          ...state.carts,
          [domainModel.cart.getCartKey(eventParams)]: eventParams,
        },
      };
    }

    case SOCKET(domainModel.cart.events.CART_UPDATED): {
      const { sessionUser: eventSessionUser, loggedInUser } = payload;
      const isItMe = loggedInUser.id === eventSessionUser.id;

      const cartRowsChanges = payload.eventParams.cartRowsChanges;
      const cartId = payload.eventParams.id;
      if (!cartRowsChanges) {
        return state;
      }

      let newState = { ...state };
      const rowsToInsert = cartRowsChanges.rowsToInsert;

      if (rowsToInsert.length > 0) {
        rowsToInsert.forEach((row: any) => {
          newState = handleCartRowInsertion(newState, cartId, row);
        });
      }

      const rowsToUpdate = cartRowsChanges.rowsToUpdate;
      if (rowsToUpdate.length > 0) {
        rowsToUpdate.forEach((row: any) => {
          newState = handleCartRowUpdate(newState, row, isItMe);
        });
      }

      const rowsToDelete = cartRowsChanges.rowsToDelete;
      if (rowsToDelete.length > 0) {
        rowsToDelete.forEach((row: any) => {
          newState = handleCartRowDeletion(newState, row, isItMe);
        });
      }

      if (isItMe) {
        return {
          ...newState,
        };
      }

      return {
        ...newState,
        cartUpdateMessage: `Varukorgen har uppdaterats av ${eventSessionUser.name}`,
      };
    }

    case SOCKET(domainModel.cart.events.CART_DEACTIVATED): {
      const {
        fullEntity,
        sessionUser: eventSessionUser,
        loggedInUser,
      } = payload;
      const newState = { ...state };

      let message = `${fullEntity.orderFullfillmentCenter.name}-varukorgen har tömts`;
      if (_.size(newState.carts) === 1) {
        message = `Varukorgen har tömts`;
      }

      let changedByUser = '';
      if (loggedInUser.id !== eventSessionUser.id) {
        changedByUser = `av ${eventSessionUser.name}`;
      }

      delete newState.carts[domainModel.cart.getCartKey(fullEntity)];
      return {
        ...newState,
        cartUpdateMessage: `${message} ${changedByUser}`.trim(),
      };
    }
    case SOCKET(domainModel.cart.events.CART_PLACED_TO_ORDER): {
      const {
        fullEntity,
        sessionUser: eventSessionUser,
        loggedInUser,
      } = payload;
      const newState = { ...state };

      let message = `${fullEntity.orderFullfillmentCenter.name}-varukorgen har lagts till order`;
      if (_.size(newState.carts) === 1) {
        message = `Varukorgen har lagts till order`;
      }

      let changedByUser = '';
      if (loggedInUser.id !== eventSessionUser.id) {
        changedByUser = `av ${eventSessionUser.name}`;
      }

      delete newState.carts[domainModel.cart.getCartKey(fullEntity)];
      return {
        ...newState,
        cartUpdateMessage: `${message} ${changedByUser}`.trim(),
      };
    }
    case SOCKET(domainModel.cart.events.CART_ABANDONED): {
      const {
        fullEntity,
        sessionUser: eventSessionUser,
        loggedInUser,
      } = payload;
      const newState = { ...state };

      let message = `Varukorg har tagits bort`;
      if (_.size(newState.carts) === 1) {
        message = `Varukorgen har tagits bort`;
      }

      let changedByUser = '';
      if (loggedInUser.id !== eventSessionUser.id) {
        changedByUser = ` av ${eventSessionUser.name}`;
      }

      delete newState.carts[domainModel.cart.getCartKey(fullEntity)];
      return {
        ...newState,
        cartUpdateMessage: `${message}${changedByUser}`,
      };
    }

    case SOCKET(domainModel.cart.events.CART_SENT_TO_EXTERNAL_MARKETPLACE): {
      const {
        fullEntity,
        sessionUser: eventSessionUser,
        loggedInUser,
      } = payload;
      const newState = { ...state };

      let message = `Varukorg har skickats till extern marknadsplats`;
      if (_.size(newState.carts) === 1) {
        message = `Varukorgen har skickats till extern marknadsplats`;
      }

      let changedByUser = '';
      if (loggedInUser.id !== eventSessionUser.id) {
        changedByUser = `av ${eventSessionUser.name}`;
      }
      const oldKey = domainModel.cart.getCartKey({
        orderFullfillmentCenter: fullEntity.orderFullfillmentCenter,
        currency: fullEntity.currency,
      });
      const newKey = domainModel.cart.getCartKey(fullEntity);

      newState.carts[newKey] = fullEntity;
      delete newState.carts[oldKey];
      return {
        ...newState,
        cartUpdateMessage: `${message} ${changedByUser}`.trim(),
      };
    }

    case SOCKET(domainModel.cart.events.CART_READY_IN_EXTERNAL_MARKETPLACE): {
      const { fullEntity } = payload;
      const newState = { ...state };

      const message = 'Varukorg är redo på extern marknadsplats';
      const cartKey = domainModel.cart.getCartKey(fullEntity);
      newState.carts[cartKey] = fullEntity;
      return {
        ...newState,
        cartUpdateMessage: message,
      };
    }

    case ACTION_TYPES.PERSISTED_CART_ROW_UPDATED: {
      const {
        quantity,
        pricedArticle,
        orderUnit,
        orderFullfillmentCenter,
        customerSubUnit,
        procurementArticle,
        supplierCustomer,
        orderedSKU,
      }: {
        quantity: number;
        pricedArticle: PricedArticleReference;
        orderUnit: string;
        orderFullfillmentCenter: OrderFullfillmentCenter;
        customerSubUnit: CustomerSubUnitInPcsServiceReference;
        procurementArticle: ProcurementArticleReference;
        supplierCustomer: SupplierCustomerInPcsServiceReference;
        orderedSKU: StockKeepingUnit | undefined;
      } = payload;

      const currency = pricedArticle.currency;
      const cartKey = domainModel.cart.getCartKey({
        orderFullfillmentCenter,
        currency,
      });
      const cart = state.carts[cartKey] || {
        orderFullfillmentCenter,
        customerSubUnit,
        supplierCustomer,
        currency,
        cartRows: [],
      };
      const cartRows = [...cart.cartRows];
      const cartRowIndex = cartRows.findIndex((row: CartRow) =>
        procurementArticle
          ? (procurementArticle.id &&
              row.procurementArticle?.id === procurementArticle.id) ||
            (procurementArticle.qvantiId &&
              row.procurementArticle?.qvantiId === procurementArticle.qvantiId)
          : // @ts-expect-error we need to type reference types
            row.pricedArticle.id === pricedArticle.id &&
            !row.procurementArticle,
      );
      if (cartRowIndex !== -1) {
        if (quantity === 0) {
          cartRows.splice(cartRowIndex, 1);
        } else {
          cartRows[cartRowIndex] = {
            ...cartRows[cartRowIndex],
            quantity,
            orderedSKU,
            pricedArticle,
            orderUnit,
          };
        }
      } else if (quantity > 0) {
        cartRows.push({
          quantity,
          orderedSKU,
          pricedArticle,
          orderUnit,
          procurementArticle,
        });
      }
      const cartUpdateMessage = getCartMessage(
        pricedArticle,
        quantity,
        orderedSKU,
      );
      return {
        ...state,
        cartUpdateMessage,
        carts: {
          ...state.carts,
          [cartKey]: { ...cart, cartRows },
        },
      };
    }
    case ACTION_TYPES.PERSISTED_CARTS_LOADED: {
      const newCarts = { ...state.carts };
      payload.carts.forEach((cart: Cart) => {
        newCarts[domainModel.cart.getCartKey(cart)] = cart;
      });
      return {
        ...state,
        carts: newCarts,
        isLoaded: true,
      };
    }
    case ACTION_TYPES.PERSISTED_CART_CREATED: {
      const cart = payload;
      return {
        ...state,
        carts: {
          ...state.carts,
          [domainModel.cart.getCartKey(cart)]: cart,
        },
      };
    }
    case ACTION_TYPES.PERSISTED_CART_COPIED_FROM_ORDER: {
      return {
        ...state,
        cartUpdateMessage: 'Order har kopierats till varukorgen',
      };
    }
    default:
      return state;
  }
};

export default persistedMultiCartReducer;
